import { Box, Button, CircularProgress, IconButton } from "@mui/material";
import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { enqueueSnackbar } from "notistack";
import Iconify from "src/components/iconify/iconify";
import axios, { endpoints } from "src/utils/axios";
import { downscaleImageFile } from "./downscale-image";

type RentalFileFieldProps = {
  fileUrl: string;
  onChange: (fileUrl: string) => void;
  pdfOnly?: boolean;
};

export function RentalFileField({ fileUrl, onChange, pdfOnly }: RentalFileFieldProps) {
  const [savingFile, setSavingFile] = useState(false);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      acceptedFiles.forEach((accepted) => {
        setSavingFile(true);
        downscaleImageFile(accepted)
          .then((file) => axios.postForm(endpoints.rentalTrailerEvents.uploadFile, { file }))
          .then((response) => response.data)
          .then((response) => {
            if (response.fileUrl) {
              enqueueSnackbar("Successfully uploaded the File", { variant: "success" });
              onChange(response.fileUrl);
            }
          })
          .catch(() => {
            enqueueSnackbar("Your file could not be uploaded. Please try again.", { variant: "error" });
          })
          .finally(() => {
            setSavingFile(false);
          });
      });
    },
    [onChange]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: false,
    accept: pdfOnly
      ? { "application/pdf": [".pdf"] }
      : { "image/png": [".png"], "image/jpeg": [".jpg", ".jpeg"], "application/pdf": [".pdf"] },
  });

  return (
    <Box display="flex" alignItems="center" gap={1}>
      {fileUrl ? (
        <>
          <Button href={fileUrl} target="_blank" rel="noopener" color="secondary" variant="outlined" size="small">
            View File
          </Button>
          <IconButton color="error" size="small" onClick={() => onChange("")}>
            <Iconify icon="solar:trash-bin-trash-bold" width={16} />
          </IconButton>
        </>
      ) : (
        <Box {...getRootProps()}>
          <input {...getInputProps()} />
          <Button variant="soft" size="small" startIcon={savingFile ? <CircularProgress size={14} /> : <Iconify icon="eva:cloud-upload-fill" />}>
            Upload
          </Button>
        </Box>
      )}
    </Box>
  );
}
